import { createSearchParams, useNavigate, useSearchParams } from 'react-router';
import { App, Form } from 'antd';
import { useTranslation } from 'translations/hooks';
import { Routes } from 'business/router/constants';
import { getLocalStorageValue } from 'technical/local-storage';
import { SCAN_SITE_KEY } from 'technical/local-storage/constants';
import { scanSiteSchema } from 'technical/local-storage/types';
import { useCreateRepairMutation } from './use-create-repair-mutation';
import type { DefectRepairCreateV1InputDto } from 'generated/apiSchemas';
import { useGetDefectRepairTypeData } from './data/use-get-defect-repair-type';
import { useGetQuarantineDefectData } from './data/use-get-quarantine-defect';
import type { RepairFormValues } from 'business/receipt/pages/repair/types';

export const useCreateRepair = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();

  const id = searchParams.get('id');
  const serialNumber = searchParams.get('serialNumber');
  const segmentType = searchParams.get('segmentType');

  const { createRepair, isLoading } = useCreateRepairMutation();

  const scanSite = getLocalStorageValue({
    key: SCAN_SITE_KEY,
    schema: scanSiteSchema,
  });

  const { data: defectRepairTypesData } = useGetDefectRepairTypeData();
  const defectRepairTypes = defectRepairTypesData?.repairTypes || [];

  const { data: quarantineDefect } = useGetQuarantineDefectData(id ?? '');

  const handleSubmit = async (row: RepairFormValues) => {
    const formData = new FormData();

    formData.append('quarantineDefectId', id ?? '');
    formData.append('scanSiteId', scanSite?.id ?? 'no-scan-site');
    formData.append('type', row.type ?? '');
    formData.append('description', row.description ?? '');

    if (row.pictures && row.pictures.length > 0) {
      row.pictures.forEach((picture) => {
        const file = picture.originFileObj;
        if (file instanceof Blob) {
          formData.append('pictures', file);
        }
      });
    }

    createRepair(
      {
        body: formData as unknown as DefectRepairCreateV1InputDto,
        headers: { 'Content-Type': 'multipart/form-data' },
      },
      {
        onSuccess: (data) => {
          if (!data) {
            return;
          }

          navigate(
            `${Routes.ReceiptSuccess}?${createSearchParams({
              serialNumber: serialNumber ?? '',
              segmentType: segmentType ?? '',
              status: data.status,
            })}`,
          );
        },
        onError: () => message.error(t('common.technicalError')),
      },
    );
  };

  return {
    form,
    serialNumber,
    segmentType,
    quarantineDefect,
    defectRepairTypes,
    handleSubmit,
    isLoading,
  };
};
